import type { DetailedCalculationResult } from './types';

/**
 * Input values for incremental room revenue calculation
 */
interface IncrementalRoomRevenueInputs {
	netRoomsAdded: number;
	rate_requested: number;
}

/**
 * Calculation steps for incremental room revenue
 */
interface IncrementalRoomRevenueSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate incremental room revenue from the net rooms added at the requested rate
 */
export const calculateIncrementalRoomRevenue = (
	netRoomsAdded: number,
	rate_requested: number,
): DetailedCalculationResult<
	number,
	IncrementalRoomRevenueInputs,
	IncrementalRoomRevenueSteps
> => {
	const result = netRoomsAdded * rate_requested;

	return {
		value: result,
		details: {
			inputValues: {
				netRoomsAdded,
				rate_requested,
			},
			calculations: {
				formula: {
					value: 'netRoomsAdded * rate_requested',
					description:
						'Calculates the incremental room revenue from net rooms added at the requested rate',
				},
				result: {
					value: result,
					description: 'The final incremental room revenue',
				},
			},
		},
	};
};

/**
 * Input values for required profit calculation
 */
interface RequiredProfitInputs {
	rooms_requested: number;
	rate_requested: number;
	profitMargin: number;
}

/**
 * Calculation steps for required profit
 */
interface RequiredProfitSteps {
	profitPercentage: { value: number; description: string };
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the required profit based on profit margin
 */
export const calculateRequiredProfit = (
	rooms_requested: number,
	rate_requested: number,
	profitMargin: number,
): DetailedCalculationResult<
	number,
	RequiredProfitInputs,
	RequiredProfitSteps
> => {
	const profitPercentage = profitMargin / 100;
	const result = rooms_requested * rate_requested * profitPercentage;

	return {
		value: result,
		details: {
			inputValues: {
				rooms_requested,
				rate_requested,
				profitMargin,
			},
			calculations: {
				profitPercentage: {
					value: profitPercentage,
					description:
						'Profit margin converted to decimal (profitMargin / 100)',
				},
				formula: {
					value: 'rooms_requested * rate_requested * profitPercentage',
					description:
						'Calculates the required profit based on rooms, rate, and profit margin percentage',
				},
				result: {
					value: result,
					description: 'The final required profit',
				},
			},
		},
	};
};

/**
 * Input values for F&B meeting benefit calculation
 */
interface FBMeetingBenefitInputs {
	fbProfit: number;
	netRooms: number;
	numberOfNights: number;
}

/**
 * Calculation steps for F&B meeting benefit
 */
interface FBMeetingBenefitSteps {
	formula: { value: string; description: string };
	result: { value: number; description: string };
}

/**
 * Calculate the F&B meeting benefit based on F&B profit per group
 */
export const calculateFBMeetingBenefit = (
	fbProfit: number,
	netRooms: number,
	meeting_space_benefit: number,
	numberOfNights: number,
): DetailedCalculationResult<
	number,
	FBMeetingBenefitInputs,
	FBMeetingBenefitSteps
> => {
	const fbProfitPerNight = numberOfNights > 0 ? fbProfit / numberOfNights : 0;
	const result = fbProfitPerNight + meeting_space_benefit;

	return {
		value: result,
		details: {
			inputValues: {
				fbProfit,
				netRooms,
				numberOfNights,
			},
			calculations: {
				formula: {
					value: '(fbProfit / numberOfNights) + meeting_space_benefit',
					description:
						'Calculates the F&B meeting benefit per night by dividing total F&B profit per group by number of nights, then adding meeting space benefit',
				},
				result: {
					value: result,
					description: 'The final F&B meeting benefit per night',
				},
			},
		},
	};
};
